/**
 * /connect/:provider/callback — OAuth return leg for social connects (TikTok now).
 *
 * Rendered at the AppShell level (like /share/:id) so it works for every cohort
 * without touching the V1/V2 route fork. Reads code+state from the query,
 * verifies the CSRF state stored by startSocialConnect, finishes the exchange
 * server-side, then sends the user back to where they clicked Connect.
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Check, Loader2, X } from 'lucide-react';
import '@/v2/i18n';
import {
  completeSocialConnect,
  consumeSocialReturnPath,
  type SocialProvider,
} from '@/v2/social';

// Support dashboard that starts brand-account connects (vivideo-social `admin-social`).
const SUPPORT_DASHBOARD_ORIGIN = 'https://vivideo-support-panel.web.app';
const BRAND_STATE_PREFIX = 'vvbrand.';

export function SocialCallbackPage() {
  const { t } = useTranslation('v2');
  const navigate = useNavigate();
  const { provider = '' } = useParams();
  // Human label for the provider (the success/fail heading used to be hardcoded
  // "TikTok"; now it names whichever platform is being connected).
  const PROVIDER_LABELS: Record<string, string> = {
    tiktok: 'TikTok', facebook: 'Facebook', instagram: 'Instagram',
    youtube: 'YouTube', twitter: 'X (Twitter)', linkedin: 'LinkedIn',
    threads: 'Threads', googledrive: 'Google Drive',
  };
  const providerLabel = PROVIDER_LABELS[provider] || provider;
  const [params] = useSearchParams();
  const [status, setStatus] = useState<'working' | 'done' | 'failed'>('working');
  const [detail, setDetail] = useState('');
  const ran = useRef(false); // StrictMode double-mount guard: the code is single-use

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    const code = params.get('code') || '';
    const state = params.get('state') || '';
    const providerError = params.get('error') || '';

    // Brand-account connects started from the internal support dashboard carry a
    // `vvbrand.` state. The platforms only redirect to this registered URL, so hand
    // the one-time code straight to the dashboard (fixed origin, never from the
    // query) and let its admin-only backend finish the exchange. User connects
    // generate random states and never enter this branch.
    if (state.startsWith(BRAND_STATE_PREFIX)) {
      const q = new URLSearchParams({ connect: provider, code, state });
      if (providerError) q.set('error', params.get('error_description') || providerError);
      window.location.replace(`${SUPPORT_DASHBOARD_ORIGIN}/?${q.toString()}`);
      return;
    }

    const returnTo = consumeSocialReturnPath();

    const finish = async () => {
      if (providerError || !code) {
        setDetail(params.get('error_description') || providerError || t('social.connectFailed'));
        setStatus('failed');
        return;
      }
      try {
        const account = await completeSocialConnect(provider as SocialProvider, code, state);
        setDetail(account.display_name ? t('social.connectedAs', { name: account.display_name }) : '');
        setStatus('done');
        setTimeout(() => navigate(returnTo, { replace: true }), 1500);
      } catch (e) {
        setDetail(e instanceof Error ? e.message : t('social.connectFailed'));
        setStatus('failed');
      }
    };
    void finish();
  }, [params, provider, navigate, t]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <div className="w-full max-w-sm rounded-2xl border border-glass-border bg-surface p-8 text-center">
        <span className="mx-auto mb-4 w-12 h-12 rounded-full bg-surface-light border border-glass-border flex items-center justify-center">
          {status === 'working' && <Loader2 className="w-6 h-6 text-primary animate-spin" />}
          {status === 'done' && <Check className="w-6 h-6 text-primary" />}
          {status === 'failed' && <X className="w-6 h-6 text-red-400" />}
        </span>
        <h1 className="text-lg font-bold text-text-primary mb-1">
          {status === 'working' && t('social.connecting')}
          {status === 'done' && t('social.connected', { name: providerLabel })}
          {status === 'failed' && t('social.connectFailed', { name: providerLabel })}
        </h1>
        {detail && <p className="text-sm text-text-tertiary">{detail}</p>}
        {status === 'failed' && (
          <button
            onClick={() => navigate('/', { replace: true })}
            className="mt-5 px-4 py-2 rounded-xl bg-surface-light border border-glass-border text-sm font-semibold text-text-primary hover:border-primary/40 transition-colors"
          >
            {t('social.backToApp')}
          </button>
        )}
      </div>
    </div>
  );
}
